import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
interface FAQItem {
  question: string;
  headline: string;
  subheadline: string;
  answer: string;
}
const faqItems: FAQItem[] = [{
  question: 'What Kind of Results Can I Expect From Your Digital Marketing Services?',
  headline: 'Proven Lift in Traffic, Leads, and Sales',
  subheadline: '29 years of expertise applied to your dealership.',
  answer: 'Our clients typically see increases in organic search traffic, lead generation, and conversion rates within the first year. For example, dealers like Boats by George and Blue Water Marine have seen significant growth in leads and visibility after switching to DealersChoice.'
}, {
  question: 'Are Your Websites AI-Ready?',
  headline: 'Built for the Next Generation of Search',
  subheadline: 'Structured data and speed that AI engines prefer.',
  answer: 'Yes. DealersChoice websites are schema-rich, fast, and index-ready, designed to show up not just in Google search results but also in AI-powered search engines and summaries.'
}, {
  question: 'Can You Help Us Avoid SEO Penalties With Sold or Archived Inventory?',
  headline: 'Smart Archiving That Protects Rankings',
  subheadline: 'Keep your site clean while boosting visibility.',
  answer: 'Yes. Our platform automatically canonicalizes or archives sold inventory to avoid SEO penalties, ensuring your site remains lean, clean, and visible to buyers.'
}, {
  question: 'Do You Provide Reporting and Analytics?',
  headline: "Full Transparency Into What's Working",
  subheadline: 'Analytics and CRM integration give you the complete picture.',
  answer: "Yes. DealersChoice websites are integrated with Google Analytics and Google Search Console dashboards. You'll receive a 30-day and 90-day post-launch review, plus CRM integrations that automatically log leads, making it easy to measure ROI."
}, {
  question: 'Do You Also Run Paid Ads and Social Media Campaigns?',
  headline: 'A Full-Service Digital Marketing Partner',
  subheadline: 'SEO, PPC, and paid social from one expert team.',
  answer: 'Yes. DealersChoice is powered by Mannix Marketing, an agency with nearly three decades of experience running SEO, PPC, and paid social campaigns that generate measurable ROI.'
}, {
  question: 'How Do SEO and PPC Help Boat Dealers Get More Leads?',
  headline: 'Be Where Your Buyers Are Searching',
  subheadline: 'Outrank competitors and capture demand with precision.',
  answer: 'SEO ensures your boats and services rank highly for relevant searches, while PPC places your inventory in front of active buyers on Google and Shopping. Together, they deliver high-intent leads that are easier to close.'
}, {
  question: 'Can You Help Market Other Parts of My Business, Like Restaurants or Hotels I Own?',
  headline: 'Whole-Business Marketing Expertise',
  subheadline: 'More than boats — we optimize your full portfolio.',
  answer: "Yes. Many of our clients also own restaurants, hotels, or marinas. Mannix Marketing's deep roots in hospitality and tourism marketing mean we can grow not just your dealership but your entire business ecosystem."
}];
function FAQItemComponent({
  item,
  isOpen,
  onToggle,
  index
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.05,
    duration: 0.4
  }} className="border-b border-gray-200 last:border-b-0" data-id="element-41">
      <button onClick={onToggle} className="w-full py-6 flex items-center justify-between text-left group" aria-expanded={isOpen} data-id="element-42">
        <h3 className="text-lg md:text-xl font-semibold text-[#0A1628] group-hover:text-[#D4AF37] transition-colors duration-200 font-playfair pr-8" data-id="element-43">
          {item.question}
        </h3>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0" data-id="element-44">
          <ChevronDown className="w-6 h-6 text-[#D4AF37]" data-id="element-45" />
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-46">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-47">
            <div className="pb-6 pl-0" data-id="element-48">
              <h4 className="text-xl font-bold text-[#D4AF37] mb-2 font-playfair" data-id="element-49">
                {item.headline}
              </h4>
              <p className="text-[#0A1628]/70 text-sm font-medium italic mb-4" data-id="element-50">
                {item.subheadline}
              </p>
              <p className="text-gray-600 leading-relaxed" data-id="element-51">{item.answer}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-24 bg-white relative overflow-hidden" data-id="element-52">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-53">
        <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-54">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4 font-playfair" data-id="element-55">
            Digital Marketing FAQ
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6" data-id="element-56"></div>
          <p className="text-gray-600 text-lg leading-relaxed" data-id="element-57">
            Get answers to the most common questions about our marine dealership
            marketing services.
          </p>
        </div>

        <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl px-6 md:px-10 py-2" data-id="element-58">
          {faqItems.map((item, index) => <FAQItemComponent key={index} item={item} isOpen={openIndex === index} onToggle={() => handleToggle(index)} index={index} data-id="element-59" />)}
        </div>
      </div>
    </section>;
}